import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Search, TrendingUp, Users, Database, BarChart3, ArrowUpRight } from "lucide-react";
import { LineReveal } from "./Reveal";

const FloatCard = ({ sx, sy, factor, className, delay, children, testid }) => {
  const x = useTransform(sx, (v) => v * 46 * factor);
  const y = useTransform(sy, (v) => v * 46 * factor);
  return (
    <motion.div
      data-testid={testid}
      style={{ x, y }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute ${className}`}
    >
      <motion.div
        animate={{ y: [0, -9, 0] }}
        transition={{ duration: 4 + factor * 2, repeat: Infinity, ease: "easeInOut" }}
        className="glass rounded-2xl px-4 py-3 shadow-2xl shadow-black/40"
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-dg-navy bg-grid-dark noise"
    >
      <div className="absolute -top-48 -left-32 h-[30rem] w-[30rem] rounded-full bg-dg-blue/30 blur-[150px]" />
      <div className="absolute bottom-0 right-0 h-[26rem] w-[26rem] rounded-full bg-dg-cyan/10 blur-[130px]" />

      <div className="relative mx-auto grid w-full max-w-7xl gap-16 px-5 sm:px-8 pt-32 pb-20 lg:grid-cols-[1.15fr_1fr] lg:items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-dg-cyan"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-dg-cyan animate-pulse" />
            Everything Digital. One Powerful Hub.
          </motion.p>

          <h1 className="font-display mt-7 text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold leading-[1.04] tracking-tight text-white">
            <LineReveal delay={0.15}>YOUR COMPLETE</LineReveal>
            <LineReveal delay={0.28}><span className="text-grad">DIGITAL GROWTH</span></LineReveal>
            <LineReveal delay={0.41}>PARTNER.</LineReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400"
          >
            From branding and websites to digital marketing, performance advertising, SEO and business
            automation — DG Hubs brings everything your business needs to grow, under one roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-col gap-4 sm:flex-row"
          >
            <button
              data-testid="hero-consultation-btn"
              onClick={() => navigate("/contact")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-dg-cyan px-8 py-4 font-bold text-dg-navy transition-all duration-300 hover:bg-white hover:shadow-[0_0_35px_rgba(0,184,212,0.45)]"
            >
              Get Free Consultation
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-explore-services-btn"
              onClick={() => navigate("/services")}
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 font-bold text-white transition-all duration-300 hover:border-dg-cyan hover:text-dg-cyan"
            >
              Explore Our Services
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-slate-500"
          >
            <span className="font-mono uppercase tracking-[0.2em]">Delhi NCR, India</span>
            <span className="hidden h-3 w-px bg-white/15 sm:block" />
            <span className="font-mono uppercase tracking-[0.2em]">Marketing × Tech × Branding × ERP</span>
          </motion.div>
        </div>

        <div className="relative hidden h-[30rem] lg:block" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-1/2 top-1/2 w-72 -translate-x-1/2 -translate-y-1/2"
          >
            <div className="glass rounded-3xl p-6 shadow-2xl shadow-black/50">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-dg-cyan">Growth Dashboard</p>
                <BarChart3 size={15} className="text-dg-cyan" />
              </div>
              <p className="font-display mt-4 text-3xl font-extrabold text-white">+182%</p>
              <p className="text-xs text-slate-400">Digital growth this year</p>
              <div className="mt-5 flex h-20 items-end gap-2">
                {[35, 55, 42, 70, 58, 85, 100].map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 1 + i * 0.09, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className={`flex-1 rounded-t-md ${i === 6 ? "bg-dg-cyan" : "bg-dg-blue2/60"}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <FloatCard sx={sx} sy={sy} factor={0.9} delay={0.9} className="left-0 top-8" testid="float-card-seo">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-dg-cyan/15 text-dg-cyan"><Search size={16} /></span>
              <div>
                <p className="font-display text-lg font-bold text-white leading-none">98<span className="text-xs text-slate-400">/100</span></p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">SEO Score</p>
              </div>
            </div>
          </FloatCard>

          <FloatCard sx={sx} sy={sy} factor={-0.7} delay={1.05} className="right-0 top-24" testid="float-card-roas">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-dg-cyan/15 text-dg-cyan"><TrendingUp size={16} /></span>
              <div>
                <p className="font-display text-lg font-bold text-white leading-none">4.2x</p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">Ads ROAS</p>
              </div>
            </div>
          </FloatCard>

          <FloatCard sx={sx} sy={sy} factor={0.6} delay={1.2} className="bottom-20 left-4" testid="float-card-leads">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-dg-cyan/15 text-dg-cyan"><Users size={16} /></span>
              <div>
                <p className="font-display text-lg font-bold text-white leading-none">+240%</p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">Qualified Leads</p>
              </div>
            </div>
          </FloatCard>

          <FloatCard sx={sx} sy={sy} factor={-1} delay={1.35} className="bottom-6 right-8" testid="float-card-erp">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-dg-cyan/15 text-dg-cyan"><Database size={16} /></span>
              <div>
                <p className="font-display text-lg font-bold text-white leading-none">99.9%</p>
                <p className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">ERP Uptime</p>
              </div>
            </div>
          </FloatCard>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 lg:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-10 w-6 rounded-full border border-white/20 p-1.5"
        >
          <div className="h-2 w-full rounded-full bg-dg-cyan" />
        </motion.div>
      </motion.div>
    </section>
  );
};
